import type { Icons } from '@eppendorf/vnls-css/dist';
import type { TFunction } from 'i18next';

import { getDeviceEventCategoryLabel } from '$shared/i18n/device-event-category-label';

import type {
  DeviceCategoriesListQuery,
  DeviceCategoryConfig,
} from '$components/app-config/app-config.types';

export function localizeDeviceCategory(
  category: DeviceCategoryConfig,
  t: TFunction,
): DeviceCategoryConfig {
  const translate = (literal: string) =>
    t(literal, { defaultValue: literal, keySeparator: false, nsSeparator: false });

  return {
    ...category,
    name: getDeviceEventCategoryLabel(category.key, t, category.name),
    description: translate(category.description),
    subCategories: category.subCategories.map((subCategory) => ({
      ...subCategory,
      name: getDeviceEventCategoryLabel(subCategory.key, t, subCategory.name),
    })),
  };
}

export function getDeviceCategoriesQuery(
  devices: { model?: string }[],
  selectedDeviceType?: string,
): DeviceCategoriesListQuery | undefined {
  const models = devices
    .map((device) => device.model)
    .filter((model): model is string => Boolean(model?.trim()));

  if (devices.length > 0 && models.length === devices.length) {
    return { models };
  }

  return selectedDeviceType?.trim() ? { deviceType: selectedDeviceType } : undefined;
}

export function getCategoryIconName(key: string): Icons {
  const normalizedKey = key.toLowerCase();

  if (normalizedKey.includes('ambient temperature')) return 'ambient-temperature';
  if (normalizedKey.includes('temperature')) return 'temperature';
  if (normalizedKey.includes('co2')) return 'co2-concentration';
  if (normalizedKey.includes('o2')) return 'o2-concentration';
  if (normalizedKey.includes('humid')) return 'relative-humidity';
  if (normalizedKey.includes('agitation')) return 'agitation';
  if (normalizedKey.includes('barometric')) return 'barometric-pressure';
  if (normalizedKey.includes('brightness')) return 'brightness';
  if (normalizedKey.includes('power')) return 'battery';
  if (normalizedKey.includes('door')) return 'door-open';
  if (normalizedKey.includes('connect')) return 'cloud-connected';
  if (normalizedKey.includes('health')) return 'maintenance';
  if (normalizedKey.includes('parameter control')) return 'alarm-limits';

  return 'placeholder';
}
